// ─────────────────────────────────────────────────────────────────────────────
// useDetectorSnapshotsMap
//
// Fetches every detector_snapshots row for the current (user, account) scope
// in a single PostgREST request, then exposes them as a Map keyed by
// `${detectorId}:${metricName}` for instant per-card lookup.
//
// Why eager + batched:
//   - One round trip on tab mount vs N round trips when each card expands.
//   - When the user clicks "Show details," the sparkline is already painted.
//     No spinner, no flash, no perceived latency.
//   - Data size is small (10 detectors × ~5 metrics × ≤90 days = <5000 rows).
//   - The data layer in detectorState.js already supports the batched read
//     via loadSnapshots(metricName=null).
//
// `sinceDays` is part of the cache key — switching the user-visible 30/60/90
// selector triggers a re-fetch automatically because the hook keys off it.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from "react";
import { loadDetectorHistory } from "../lib/detectorState";

export function useDetectorSnapshotsMap({ userId, accountId, sinceDays = 30 }) {
  const [map, setMap] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId) {
      setMap(new Map());
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        // PostgREST request for ALL detectors in one shot. We pass
        // detectorId='' to loadDetectorHistory? No — loadDetectorHistory needs
        // a detectorId. We need a sibling that fetches across detectors. The
        // simplest path: do a flat fetch via loadDetectorHistory loop is bad,
        // so we issue our own bare query through the same authFetch.
        //
        // Implementation note: rather than add yet another export to
        // detectorState.js, we issue a minimal direct query here. This hook
        // is one of the few callers that needs the cross-detector view.
        const rows = await loadAllSnapshots({ userId, accountId, sinceDays });
        if (cancelled) return;

        const m = new Map();
        for (const r of rows) {
          const key = `${r.detector_id}:${r.metric_name}`;
          if (!m.has(key)) m.set(key, []);
          m.get(key).push({ date: r.snapshot_date, value: Number(r.metric_value) });
        }
        // Each series is already in date ASC order because we sort server-side,
        // but be defensive in case PostgREST returns out of order.
        for (const series of m.values()) {
          series.sort((a, b) => a.date.localeCompare(b.date));
        }
        setMap(m);
      } catch (err) {
        if (cancelled) return;
        console.error("[useDetectorSnapshotsMap] load failed:", err);
        setError(err);
        setMap(new Map());
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [userId, accountId, sinceDays]);

  return { map, loading, error };
}

// ─── Direct fetcher ──────────────────────────────────────────────────────────
// Inlined here so we don't need to extend detectorState.js's public API just
// for this one cross-detector query. Mirrors loadSnapshots() but without the
// detectorId filter.
async function loadAllSnapshots({ userId, accountId, sinceDays }) {
  const { SUPABASE_URL, authFetch } = await import("../lib/supabase");
  const since = new Date();
  since.setDate(since.getDate() - sinceDays);
  const sinceStr = since.toISOString().slice(0, 10);

  const acctFilter = (accountId === null || accountId === undefined)
    ? "account_id=is.null"
    : `account_id=eq.${encodeURIComponent(accountId)}`;

  const params = [
    `user_id=eq.${encodeURIComponent(userId)}`,
    acctFilter,
    `snapshot_date=gte.${sinceStr}`,
    `select=detector_id,metric_name,snapshot_date,metric_value`,
    `order=snapshot_date.asc`,
    `limit=10000`,
  ].join("&");

  const res = await authFetch(`${SUPABASE_URL}/rest/v1/detector_snapshots?${params}`);
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`loadAllSnapshots ${res.status}: ${body}`);
  }
  return res.json();
}

// ─── Series accessor ─────────────────────────────────────────────────────────
// Helper for consumers: pull the series for a given detector+metric out of
// the map, returning an empty array if missing. Saves callers from writing
// `map.get(...) || []` everywhere.
export function getSeries(map, detectorId, metricName) {
  if (!map || !detectorId || !metricName) return [];
  return map.get(`${detectorId}:${metricName}`) || [];
}
